/**
 * Sales Detail Modal
 * 영업 리드 상세 모달
 */

import React, { useEffect, useState } from 'react';
import { useSales } from '../../use-cases/useSales';
import { AdminUser } from '../../models';
import {
  SalesLead,
  SalesStatus,
  getSalesStatusText,
  formatDate,
  formatDateTime,
  formatCurrency,
  canChangeStatus,
  isLeadFinal,
} from '../../models/SalesLead';
import { SalesActivityTab } from './SalesActivityTab';

interface SalesDetailModalProps {
  lead: SalesLead;
  currentUser: AdminUser | null;
  onClose: () => void;
}

type TabType = 'info' | 'activities' | 'edit';

export const SalesDetailModal: React.FC<SalesDetailModalProps> = ({
  lead,
  currentUser,
  onClose,
}) => {
  const {
    selectedLead,
    activities,
    loading,
    error,
    fetchLead,
    fetchActivities,
    updateLead,
    updateStatus,
    addActivity,
    clearError,
  } = useSales();

  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [editMode, setEditMode] = useState(false);
  const [statusChangeMode, setStatusChangeMode] = useState(false);
  const [lostReason, setLostReason] = useState('');

  // 편집 폼 상태
  const [editForm, setEditForm] = useState({
    company_name: lead.company_name,
    contact_name: lead.contact_name,
    contact_email: lead.contact_email || '',
    contact_phone: lead.contact_phone,
    address: lead.address || '',
    expected_contract_date: lead.expected_contract_date || '',
    contract_amount: lead.contract_amount?.toString() || '',
    notes: lead.notes || '',
  });

  useEffect(() => {
    fetchLead(lead.id);
    fetchActivities(lead.id);
  }, [lead.id, fetchLead, fetchActivities]);

  const displayLead = selectedLead || lead;

  const handleStatusChange = async (newStatus: SalesStatus) => {
    if (newStatus === 'lost' && !lostReason.trim()) {
      alert('영업 실패 사유를 입력해주세요.');
      return;
    }

    const reason = newStatus === 'lost' ? lostReason : undefined;
    const success = await updateStatus(lead.id, newStatus, reason);
    if (success) {
      setStatusChangeMode(false);
      setLostReason('');
    }
  };

  const handleSaveEdit = async () => {
    const success = await updateLead(lead.id, {
      company_name: editForm.company_name,
      contact_name: editForm.contact_name,
      contact_email: editForm.contact_email || undefined,
      contact_phone: editForm.contact_phone,
      address: editForm.address || undefined,
      expected_contract_date: editForm.expected_contract_date || undefined,
      contract_amount: editForm.contract_amount ? parseFloat(editForm.contract_amount) : undefined,
      notes: editForm.notes || undefined,
    });
    if (success) {
      setEditMode(false);
    }
  };

  const getStatusBadgeColor = (status: string): string => {
    const colors: Record<string, string> = {
      consulting: 'bg-blue-100 text-blue-800',
      quote_sent: 'bg-yellow-100 text-yellow-800',
      negotiating: 'bg-orange-100 text-orange-800',
      contracted: 'bg-green-100 text-green-800',
      lost: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const availableStatuses: SalesStatus[] = ['consulting', 'quote_sent', 'negotiating', 'contracted', 'lost'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{displayLead.company_name}</h2>
            <div className="flex items-center space-x-3 mt-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(displayLead.status)}`}>
                {getSalesStatusText(displayLead.status)}
              </span>
              {displayLead.assigned_to_name && (
                <span className="text-sm text-gray-500">👤 {displayLead.assigned_to_name}</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex px-6">
            {[
              { id: 'info', label: '기본 정보', icon: '📋' },
              { id: 'activities', label: '상담 히스토리', icon: '📝' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`px-4 py-3 border-b-2 font-medium text-sm mr-4 ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex justify-between items-center">
            <span>{error}</span>
            <button onClick={clearError} className="text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          )}

          {!loading && activeTab === 'info' && (
            <div className="space-y-6">
              {/* Status Change */}
              {!isLeadFinal(displayLead) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">상태 변경</span>
                    {!statusChangeMode ? (
                      <button
                        onClick={() => setStatusChangeMode(true)}
                        className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700"
                      >
                        상태 변경
                      </button>
                    ) : (
                      <button
                        onClick={() => setStatusChangeMode(false)}
                        className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                      >
                        취소
                      </button>
                    )}
                  </div>

                  {statusChangeMode && (
                    <div className="mt-4 space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {availableStatuses.map((status) => (
                          <button
                            key={status}
                            onClick={() => status !== 'lost' && handleStatusChange(status)}
                            disabled={!canChangeStatus(displayLead, status)}
                            className={`px-3 py-2 rounded text-sm font-medium ${
                              canChangeStatus(displayLead, status)
                                ? status === 'lost'
                                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {getSalesStatusText(status)}
                          </button>
                        ))}
                      </div>

                      {/* Lost Reason */}
                      <div className="border-t pt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          영업 실패 사유 (실패 처리 시 필수)
                        </label>
                        <textarea
                          value={lostReason}
                          onChange={(e) => setLostReason(e.target.value)}
                          placeholder="영업 실패 사유를 입력하세요..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          rows={2}
                        />
                        <button
                          onClick={() => handleStatusChange('lost')}
                          disabled={!lostReason.trim()}
                          className={`mt-2 px-4 py-2 rounded text-sm font-medium ${
                            lostReason.trim()
                              ? 'bg-red-600 text-white hover:bg-red-700'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          영업 실패 처리
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Lead Info */}
              {!editMode ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-500">업체명</label>
                      <p className="text-lg font-medium">{displayLead.company_name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">담당자명</label>
                      <p className="text-lg">{displayLead.contact_name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">연락처</label>
                      <p className="text-lg">{displayLead.contact_phone}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">이메일</label>
                      <p className="text-lg">{displayLead.contact_email || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">주소</label>
                      <p className="text-lg">{displayLead.address || '-'}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-500">예상 계약 금액</label>
                      <p className="text-lg font-medium text-green-600">
                        {formatCurrency(displayLead.contract_amount)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">예상 계약일</label>
                      <p className="text-lg">{formatDate(displayLead.expected_contract_date)}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">등록일</label>
                      <p className="text-lg">{formatDateTime(displayLead.created_at)}</p>
                    </div>
                    {displayLead.contracted_at && (
                      <div>
                        <label className="text-sm text-gray-500">계약 완료일</label>
                        <p className="text-lg">{formatDateTime(displayLead.contracted_at)}</p>
                      </div>
                    )}
                    {displayLead.lost_reason && (
                      <div>
                        <label className="text-sm text-gray-500">영업 실패 사유</label>
                        <p className="text-lg text-red-600">{displayLead.lost_reason}</p>
                      </div>
                    )}
                  </div>

                  {displayLead.notes && (
                    <div className="md:col-span-2">
                      <label className="text-sm text-gray-500">메모</label>
                      <p className="text-lg whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                        {displayLead.notes}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* Edit Form */
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">업체명 *</label>
                      <input
                        type="text"
                        value={editForm.company_name}
                        onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">담당자명 *</label>
                      <input
                        type="text"
                        value={editForm.contact_name}
                        onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">연락처 *</label>
                      <input
                        type="text"
                        value={editForm.contact_phone}
                        onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                      <input
                        type="email"
                        value={editForm.contact_email}
                        onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">주소</label>
                      <input
                        type="text"
                        value={editForm.address}
                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예상 계약 금액</label>
                      <input
                        type="number"
                        value={editForm.contract_amount}
                        onChange={(e) => setEditForm({ ...editForm, contract_amount: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">예상 계약일</label>
                      <input
                        type="date"
                        value={editForm.expected_contract_date}
                        onChange={(e) => setEditForm({ ...editForm, expected_contract_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                    <textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {!loading && activeTab === 'activities' && (
            <SalesActivityTab
              leadId={lead.id}
              activities={activities}
              onAddActivity={addActivity}
              onRefresh={() => fetchActivities(lead.id)}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-between">
          <div>
            {activeTab === 'info' && !isLeadFinal(displayLead) && (
              <>
                {!editMode ? (
                  <button
                    onClick={() => setEditMode(true)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    ✏️ 정보 수정
                  </button>
                ) : (
                  <div className="space-x-2">
                    <button
                      onClick={() => setEditMode(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                    >
                      저장
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};
